import { memo } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  EditorHeaderLanguageVersions,
  EditorHeaderStatus,
  EditorHeaderPublish,
  EditorHeaderSlug
} from '.';

import type { GetCourseTranslationProgressRes } from 'shared-types/responses';
import type { EditorHeaderStateProps } from './EditorHeader';

export type EditorHeaderChapterStateProps = Pick<
  EditorHeaderStateProps,
  | 'activeLanguageVersion'
  | 'currentChapter'
  | 'isDraft'
  | 'isPublishing'
  | 'languageVersions'
  | 'previewType'
  | 'previewVersionCourseLanguage'
  | 'previewVersionCourseLanguageVersions'
  | 'publishDate'
  | 'status'
  | 'translationReferenceLanguage'
  | 'updatedAt'
>;

interface Props extends EditorHeaderChapterStateProps {
  showInfo?: boolean;
  translationProgressData?: GetCourseTranslationProgressRes['progressByLanguage'];
}

const EditorHeaderChapter = ({
  activeLanguageVersion,
  currentChapter,
  isDraft,
  isPublishing,
  languageVersions,
  previewType,
  previewVersionCourseLanguage,
  previewVersionCourseLanguageVersions,
  publishDate,
  showInfo = true,
  status,
  translationProgressData,
  translationReferenceLanguage,
  updatedAt
}: Props) => {
  const {
    pageState: { course },
    theme
  } = useReduxState();

  return (
    <div className="chapter">
      <div className="wrapper">
        <div className="number">{currentChapter.number}</div>

        <div className="title-wrapper">
          <div className="title">
            <div className="title-text">{currentChapter.title}</div>
          </div>

          <div className="slug">
            <EditorHeaderSlug
              currentChapter={currentChapter}
              previewType={previewType}
            />
          </div>
        </div>
      </div>

      {showInfo ? (
        <div className="info">
          <div className="info-row">
            <div className="status">
              <EditorHeaderStatus
                status={status}
                updatedAt={updatedAt}
                isDraft={isDraft}
                previewType={previewType}
              />
            </div>

            <div className="separator" />

            <div className="published">
              <EditorHeaderPublish
                currentChapter={currentChapter}
                isPublishing={isPublishing}
                previewType={previewType}
                publishDate={publishDate}
              />
            </div>

            {Object.keys(languageVersions || course.languageVersions).length >
              0 &&
              translationProgressData && (
                <>
                  <div className="separator" />

                  <div className="language-versions language-versions--inline">
                    <EditorHeaderLanguageVersions
                      activeLanguageVersion={activeLanguageVersion}
                      previewType={previewType}
                      previewVersionCourseLanguage={
                        previewVersionCourseLanguage
                      }
                      previewVersionCourseLanguageVersions={
                        previewVersionCourseLanguageVersions
                      }
                      translationProgressData={translationProgressData}
                      translationReferenceLanguage={
                        translationReferenceLanguage
                      }
                    />
                  </div>
                </>
              )}
          </div>

          {Object.keys(languageVersions || course.languageVersions).length >
            0 &&
            translationProgressData && (
              <div className="language-versions language-versions--below">
                <EditorHeaderLanguageVersions
                  activeLanguageVersion={activeLanguageVersion}
                  previewType={previewType}
                  previewVersionCourseLanguage={previewVersionCourseLanguage}
                  previewVersionCourseLanguageVersions={
                    previewVersionCourseLanguageVersions
                  }
                  translationProgressData={translationProgressData}
                  translationReferenceLanguage={translationReferenceLanguage}
                />
              </div>
            )}
        </div>
      ) : null}

      <style jsx>{`
        .wrapper {
          display: flex;
        }
        .number {
          background-color: ${theme.lightGrey};
          border-radius: 50%;
          color: ${theme.textColor};
          flex-grow: 0;
          flex-shrink: 0;
          font-size: 1.3rem;
          font-weight: 700;
          height: 22px;
          margin-right: 6px;
          padding: 2px 0;
          text-align: center;
          width: 22px;
        }
        .title-wrapper {
          max-width: 90%;
        }
        .title {
          color: ${theme.textColor};
          font-size: 1.4rem;
          font-weight: 700;
          line-height: 1.2;
        }
        .title-text {
          display: -webkit-box;
          overflow: hidden;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
        .slug {
          position: relative;
          top: -3px;
        }
        .info {
          color: ${theme.textColorLighter};
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          font-size: 1.3rem;
          line-height: 1.2;
          margin-left: 28px;
          position: relative;
        }
        .info-row {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        .status {
          margin-top: 1px;
        }
        .separator {
          background-color: #ddd;
          display: inline-block;
          height: 12px;
          margin: 3px 10px;
          width: 1px;
        }
        .language-versions--inline {
          display: block;
        }
        .language-versions--below {
          display: none;
        }
        @media (min-width: ${breakpoints.large}px) {
          .title-wrapper {
            max-width: 280px;
          }
        }
        @media (min-width: ${breakpoints.huge}px) {
          .title-wrapper {
            max-width: 350px;
          }
        }
        @media (max-width: ${breakpoints.large - 1}px) {
          .language-versions--inline {
            display: none;
          }
          .language-versions--below {
            display: block;
            margin-top: 10px;
            margin-left: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default memo(EditorHeaderChapter);
